import { useState } from 'react';
import { Box, Button, Typography, Alert } from '@mui/material';
import { useDispatch } from 'react-redux';
import { CommonDialog, CommonDialogHeader } from '..';
import { ITemporaryPasswordResponse } from '../../../types/api/response/adminusersresponse';
import { showNotification } from '../../../store/Reducers/NotificationSnackbarSlice';

interface Props {
  result: ITemporaryPasswordResponse;
  isOpenDialog: boolean;
  setIsOpenDialog: (open: boolean) => void;
}

const Field = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" fontWeight={600}>
      {label}
    </Typography>
    <Typography variant="body2" mt={0.25}>
      {value}
    </Typography>
  </Box>
);

export const TemporaryPasswordDialog = ({ result, isOpenDialog, setIsOpenDialog }: Props) => {
  const dispatch = useDispatch();
  const [hasCopied, setHasCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(result.temporary_password);
      setHasCopied(true);
      dispatch(showNotification({ message: 'Temporary password copied to clipboard.' }));
    } catch (error: any) {
      dispatch(
        showNotification({
          message: 'Failed to copy. Please copy the password manually.',
          severity: 'error',
        }),
      );
    }
  };

  const requestClose = () => {
    if (!hasCopied) {
      dispatch(
        showNotification({
          message: 'You must copy the temporary password before closing this window.',
          severity: 'warning',
        }),
      );
      return;
    }

    setIsOpenDialog(false);
  };

  return (
    <CommonDialog
      isOpen={isOpenDialog}
      header={
        <CommonDialogHeader
          title={`Temporary Password — ${result.username}`}
          onClose={requestClose}
        />
      }
      body={
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
          <Alert severity="warning">
            This password is shown once. Share it with the user now — they will be required to set a
            new password on their next login.
          </Alert>
          <Field label="Username" value={result.username} />
          <Field
            label="Temporary Password"
            value={
              <Typography variant="body1" fontFamily="monospace" fontWeight={700}>
                {result.temporary_password}
              </Typography>
            }
          />
          <Field label="Expires" value={new Date(result.password_expires_at).toLocaleString()} />
        </Box>
      }
      footer={
        <Box display="flex" gap={1} justifyContent="flex-end" width="100%">
          <Button variant="outlined" onClick={requestClose}>
            Close
          </Button>
          <Button variant="contained" onClick={handleCopy}>
            Copy Password
          </Button>
        </Box>
      }
    />
  );
};
